import React from "react";
import { AlertTriangle, Search, Users } from "lucide-react";
import CustomSelect from "../../common/customSelect/customSelect";
import { formatDate, formatMoney, formatPhone, initials, fill } from "../reportsFormat";

const SORTS = ["lastVisit", "visits", "revenue", "name"];

const CustomersPanel = ({ data, search, onSearchChange, sort, onSortChange, onOpen, onPage, t, language }) => {
	const { customers, pagination, currency, dataQuality } = data;

	const sortLabel = {
		lastVisit: t.customers.sortLastVisit,
		visits: t.customers.sortVisits,
		revenue: t.customers.sortRevenue,
		name: t.customers.sortName,
	};

	const displayName = (c) =>
		c.isAnonymousBucket ? t.customers.anonymousBucket : c.name || c.email || t.customers.noName;

	/* Whatever identifies this person under their name. The phone is grouped
	   rather than printed as the raw run of digits the booking stored — it is
	   the same number staff read back down a phone line. */
	const subtitle = (c) => {
		if (c.email) return c.email;
		if (c.phone) return formatPhone(c.phone);
		return c.isRegistered ? t.customers.registered : t.customers.guest;
	};

	const searching = search.trim().length > 0;

	return (
		<>
			{/* Search and sort share one row: the sort field used to drop below the
			    search box, which spent a whole line on one control and left the
			    space beside the search empty. */}
			<div className="sf-rap-toolbar">
				<label className="sf-rap-search">
					<Search size={16} />
					<input
						type="text"
						value={search}
						placeholder={t.customers.search}
						onChange={(e) => onSearchChange(e.target.value)}
					/>
				</label>

				{/* A div rather than a label: CustomSelect renders divs, not a form
				    control, so a label would have nothing to focus on click.
				    --inline lays the caption beside the control instead of above
				    it, so it sits level with the search box on the same row. */}
				<div className="sf-rap-field sf-rap-field--inline">
					<span>{t.customers.sort}</span>
					<CustomSelect
						className="sf-rap-select"
						value={sort}
						onChange={onSortChange}
						options={SORTS.map((s) => ({ value: s, label: sortLabel[s] }))}
					/>
				</div>
			</div>

			{dataQuality?.truncated && (
				<p className="sf-rap-inline-note">
					<AlertTriangle size={14} />
					{t.customers.truncated}
				</p>
			)}

			{customers.length === 0 ? (
				/* An empty search result and an empty client book are different
				   problems, and telling someone "no clients yet" while they can see
				   their own search text made the list look broken. */
				<div className="sf-rap-empty-state">
					<span className="sf-rap-empty-icon">{searching ? <Search size={26} /> : <Users size={26} />}</span>
					<p className="sf-rap-empty-title">{searching ? t.customers.emptySearch : t.customers.empty}</p>
					<p className="sf-rap-empty-hint">{searching ? t.customers.emptySearchHint : t.customers.emptyHint}</p>
				</div>
			) : (
				<>
					<div className="sf-rap-panel">
						<div className="sf-rap-table-wrap">
							<table className="sf-rap-table sf-rap-table--clickable">
								<thead>
									<tr>
										<th>{t.customers.name}</th>
										<th className="sf-rap-num">{t.customers.visits}</th>
										<th className="sf-rap-hide-xs">{t.customers.lastVisit}</th>
										<th className="sf-rap-num">{t.customers.spent}</th>
										<th className="sf-rap-hide-sm">{t.customers.favorite}</th>
									</tr>
								</thead>
								<tbody>
									{customers.map((c) => (
										<tr
											key={c.customerKey}
											tabIndex={0}
											role="button"
											onClick={() => onOpen(c.customerKey)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													onOpen(c.customerKey);
												}
											}}
										>
											<td>
												<div className="sf-rap-person">
													<span className={`sf-rap-avatar ${c.isAnonymousBucket ? "is-anon" : ""}`}>
														{c.isAnonymousBucket ? "?" : initials(c.name || c.email)}
													</span>
													<span className="sf-rap-person-text">
														<strong>{displayName(c)}</strong>
														<em>{subtitle(c)}</em>
														{/* Four columns do not fit a phone. Below 560px the
														    "Last visit" column is hidden and its date appears
														    here instead, where there is room for it. */}
														<span className="sf-rap-person-meta">
															{t.customers.lastVisit}: {formatDate(c.lastVisit, language)}
														</span>
													</span>
												</div>
											</td>
											<td className="sf-rap-num">{c.totalBookings}</td>
											<td className="sf-rap-hide-xs">{formatDate(c.lastVisit, language)}</td>
											<td className="sf-rap-num sf-rap-money">{formatMoney(c.lifetimeValue, currency)}</td>
											<td className="sf-rap-hide-sm">{c.favoriteService || "—"}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>

					<div className="sf-rap-pager">
						<span>{fill(t.customers.showing, { shown: customers.length, total: pagination.total })}</span>
						<div className="sf-rap-pager-btns">
							<button
								type="button"
								className="sf-rap-btn"
								disabled={pagination.offset === 0}
								onClick={() => onPage(Math.max(pagination.offset - pagination.limit, 0))}
							>
								{t.customers.prev}
							</button>
							<button
								type="button"
								className="sf-rap-btn"
								disabled={pagination.nextOffset === null}
								onClick={() => onPage(pagination.nextOffset)}
							>
								{t.customers.next}
							</button>
						</div>
					</div>
				</>
			)}
		</>
	);
};

export default CustomersPanel;
